import { Enums, Tables } from '@tralent/database';
import { addDays, addMinutes, addMonths, getTime, parseISO } from 'date-fns';
import { calendarPath, dashboardPath, myTournamentsPath, settingsPath } from '@/lib/_utils/routing.ts';

import { ResolutionString } from 'charting_library';
import { VariantTypes } from './basic-variants';
import { defaultCurrency } from '@tralent/frontend';

export const isProduction = process.env.NODE_ENV === 'production';

export type TimeFrameValueProps = '1m' | '5m' | '15m' | '30m' | '1H' | '4H' | '1D' | '1W' | '1M' | '3M' | '6M' | '12M';

interface RangeOptions {
	val: {
		type: 'period-back' | 'range-period';
		value: TimeFrameValueProps;
	};
	res: ResolutionString;
}

export const validKycPoaStatuses = ['validated', 'approved'];

export const balanceHiddenPlaceholder = `${defaultCurrency.symbol}*******.**`;

export const numToPctFormat = (value: number | null | undefined, decimal: number | undefined = 2) => {
	return `${value ? Number(value)?.toFixed(decimal) : 0}%`;
};

export const resampleFreqList: RangeOptions[] = [
	{
		val: { type: 'period-back', value: '1m' },
		res: '1' as ResolutionString,
	},

	{
		val: { type: 'period-back', value: '5m' },
		res: '5' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '15m' },
		res: '15' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '30m' },
		res: '30' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '1H' },
		res: '60' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '4H' },
		res: '240' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '1D' },
		res: '1D' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '1W' },
		res: '1W' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '1M' },
		res: '1M' as ResolutionString,
	},
	{
		val: { type: 'period-back', value: '12M' },
		res: '12M' as ResolutionString,
	},
];

export const findResampleFreqByValue = (value: TimeFrameValueProps): RangeOptions => {
	return resampleFreqList.find((item) => item.val.value === value) ?? resampleFreqList[0];
};

//export const regexPassword = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^\w\d\s]).{8,16}$/;
export const regexPassword = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[_\W]).{8,16}$/;
export const regexUsername = /^[a-zA-Z][a-zA-Z0-9_]{2,14}$/;

export function animationClass(state: string, enterClass: string, exitClass: string) {
	if (state === 'entering') return enterClass;
	if (state === 'exiting') return exitClass;
	return '';
}

export function removeNumberFromString(string: string) {
	return string.replace(/[0-9]/g, '');
}

export const loremIpsumText = [
	'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. ',
];

export const timeFrameMap = {
	'5MIN': '60',
	'30MIN': '60',
	'1H': '60',
	'4H': '1D',
	'1D': '1D',
	'1W': '1W',
	CUSTOM: '60',
};

export const getResolutionNextBarTime = (barTime: number): Record<string, number> => {
	return {
		'1': getNextMinuteBarTime(barTime, 1),
		'5': getNextMinuteBarTime(barTime, 5),
		'15': getNextMinuteBarTime(barTime, 15),
		'30': getNextMinuteBarTime(barTime, 30),
		'60': getNextMinuteBarTime(barTime, 60),
		'4H': getNextMinuteBarTime(barTime, 240),
		'1D': getNextDailyBarTime(barTime, 1),
		'1W': getNextDailyBarTime(barTime, 7),
		'1M': getNextMontlyBarTime(barTime, 1),
		'3M': getNextMontlyBarTime(barTime, 3),
		'6M': getNextMontlyBarTime(barTime, 6),
		'12M': getNextMontlyBarTime(barTime, 12),
	};
};

export function getNextDailyBarTime(barTime: number, daily: number) {
	const date = new Date(barTime);
	const nextDate = addDays(date, daily);
	return nextDate.getTime();
}

export function getNextMontlyBarTime(barTime: number, months: number) {
	const date = new Date(barTime);
	const nextDate = addMonths(date, months);
	return nextDate.getTime();
}

export function getNextMinuteBarTime(barTime: number, minuteInterval: number) {
	const date = new Date(barTime);
	const nextDate = addMinutes(date, minuteInterval);
	return nextDate.getTime();
}

export const translateTournamentType = (tournamentType: Enums<'tournament_types'>) => {
	if (tournamentType === 'CUSTOM') {
		return 'custom_duration';
	}

	const typeMap: { [key: string]: string } = {
		MIN: 'Minute',
		H: 'Hour',
		D: 'Day',
		W: 'Week',
	};

	// extract the number and unit parts
	const matches = tournamentType.match(/(\d+)([a-zA-Z]+)/);

	// Check if matches are found
	if (!matches) {
		throw new Error('Invalid tournament type format');
	}

	const [, number, unit] = matches;

	// Validate the unit
	if (!(unit.toUpperCase() in typeMap)) {
		throw new Error(`Unsupported unit '${unit}'`);
	}

	// Translate the unit and return the result
	const translatedUnit = typeMap[unit.toUpperCase()];
	const pluralSuffix = parseFloat(number) > 1 ? 's' : '';

	return `${number} ${translatedUnit}${pluralSuffix}`;
};

export const orderTypeView = (value: 'buy' | 'sell'): { label: string; variant: VariantTypes } => {
	return { label: value, variant: value === 'buy' ? 'success' : 'danger' };
};

export const numberFormatter = (format: string = 'de-DE', minFraction: number = 2, maxFraction: number = 2) =>
	new Intl.NumberFormat(format, {
		style: 'decimal',
		minimumFractionDigits: minFraction,
		maximumFractionDigits: maxFraction,
	});

export const numberFormatShort = (value: number) => {
	return new Intl.NumberFormat('en-US', {
		notation: 'compact',
		compactDisplay: 'short',
	}).format(value);
};


export const getIconNameByTournamentType = (type?: Enums<'tournament_types'>) => {
	switch (type) {
		case '5MIN':
			return 'tournamentQuickIcon';
		case '30MIN':
			return 'tournamentQuickIcon';
		case '1H':
			return 'tournament1HourIcon';
		case '4H':
			return 'tournament4HourIcon';
		case '1D':
			return 'tournamentDailyIcon';
		case '1W':
			return 'tournamentWeeklyIcon';
		case 'CUSTOM':
			return 'tournamentCustomIcon';
		default:
			return 'tournamentWeeklyIcon';
	}
};

export const getLabelSlugByTournamentType = (type?: Enums<'tournament_types'>) => {
	switch (type) {
		case '5MIN':
			return '5_minutes';
		case '30MIN':
			return '30_minutes';
		case '1H':
			return 'hourly';
		case '4H':
			return '4_hours';
		case '1D':
			return 'daily';
		case '1W':
			return 'weekly';
		case 'CUSTOM':
			return 'special_edition';
		default:
			return 'special_edition';
	}
};

type TimeUnit = 'M' | 'H' | 'D' | 'W';
type RawUnit = 'MIN' | 'H' | 'D' | 'W';

export function getTimeMapping(type: Enums<'tournament_types'>) {
	const match = type.match(/^(\d+)([A-Z]+)$/);

	if (type === 'CUSTOM') {
		return {
			value: 0,
			unit: '',
		};
	}

	if (!match) {
		throw new Error(`Invalid ${type}`);
	}

	const [, value, rawUnit] = match;

	const unitMap: Record<RawUnit, TimeUnit> = {
		MIN: 'M',
		H: 'H',
		D: 'D',
		W: 'W',
	};

	if (!(rawUnit in unitMap)) {
		throw new Error(`Invalid unit: ${rawUnit}`);
	}

	return {
		value: Number(value),
		unit: unitMap[rawUnit as RawUnit],
	};
}

export function formatCountDownValue(value: number) {
	return value >= 0 && value < 10 ? '0' + value : value;
}

export function getInfoByOrderCloseMode(order_close_modes: Enums<'order_close_modes'>) {
	switch (order_close_modes) {
		case 'sl':
			return { label: 'Stop loss' };
		case 'tp':
			return { label: 'Take profit' };
		default:
			return { label: 'Manual' };
	}
}

export function canUserUnjoinTournament({
	unjoin_end_at,
	status,
}: {
	unjoin_end_at: Tables<'tournaments'>['unjoin_end_at'];
	status: Tables<'tournaments'>['status'];
}) {
	const unjoinEndAtDate = parseISO(unjoin_end_at ?? '');
	const now = new Date();
	const isAfterOrEqual = getTime(unjoinEndAtDate) >= getTime(now);

	return status === 'pending' && isAfterOrEqual;
}

export function profitColorSwitch(value: number) {
	if (value === 0) return 'white';
	if (value > 0) return 'success';
	if (value < 0) return 'danger';
}

export const getRankAcronim = (rank: number) => {
	switch (rank) {
		case 1:
			return 'st';
		case 2:
			return 'nd';
		case 3:
			return 'rd';
		default:
			return 'th';
	}
};

export const getTournamentStatusInfo = (status: Enums<'tournament_statuses'>, isUserJoined?: boolean): VariantTypes => {
	if (isUserJoined != undefined && isUserJoined === false) {
		return 'white';
	} else {
		switch (status) {
			case 'pending':
			case 'starting':
				return 'warning';
			case 'inprogress':
				return 'success';
			case 'finished':
				return 'danger';
			case 'canceled':
				return 'tertiary';
		}
	}
};

export const getTextColorByRank = (rank: number) => {
	switch (rank) {
		case 1:
			return 'text-[#C5B848]';
		case 2:
			return 'text-[#A6A6A6]';
		case 3:
			return 'text-[#b77c38]';
		default:
			return 'text-white';
	}
};

export function createRateLimiter(delay: number) {
	const queue: (() => void)[] = [];
	let lastCalled: number | null = null;
	const delayInMs = delay * 1000;

	const dequeue = () => {
		if (queue.length === 0) return;

		const now = Date.now();
		if (lastCalled && now - lastCalled < delayInMs) {
			// If we have called a function less than delay seconds ago, drop the request
			queue.shift();
		} else {
			const func = queue.shift();
			if (func) func();
			lastCalled = now;
		}
	};

	const call = (func: () => void) => {
		queue.push(func);
		dequeue();
	};

	return { call };
}

export const copyInClipboard = (textToCopy: string) => {
	navigator.clipboard.writeText(textToCopy);
};

export const BOTTOM_NAV_ALLOWED_ROUTES = ['/', dashboardPath, myTournamentsPath, calendarPath, settingsPath];
